import React, { useState, useEffect, useRef, useCallback, useId, useContext } from "react";
import { Mail, X, Calendar, Clock, Send } from "lucide-react";
import "./HostRecruitment.css";
import { Button, Spinner, Table } from "react-bootstrap";
import { message, Tooltip, Modal } from "antd";
import { CaretDown, CaretUp } from "react-bootstrap-icons";
import InfiniteScroll from "react-infinite-scroll-component";
import HostRecruitmentContext from "context/HostRecruitmentContext";
import { sendHostInvites } from "Services/storeService";
import { convertUTCToLocalAmPmFormat } from "utils/dateFormatter";

/**
 * HostRecruitment Component
 * Displays a searchable, sortable customer table for selecting and
 * sending host-recruitment email invites.
 */
const HostRecruitment = () => {
  // ── Context ──
  const {
    storeId,
    hosts,
    hostsLoading,
    currentPage,
    hasMore,
    fetchNonHosts,
  } = useContext(HostRecruitmentContext);

  // ── Local state ──
  const [messageApi, contextHolder] = message.useMessage();
  const scrollableDivId = useId().replace(/:/g, "") + "-scrollable";
  const headerCheckboxRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [sorting, setSorting] = useState({
    name: "none",
    email: "none",
    amount: "none",
    //invitation: "none",
  });
  const [selectedHostIds, setSelectedHostIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  //const [showInvitationModal, setShowInvitationModal] = useState(false);
  //const [selectedInvitationHistory, setSelectedInvitationHistory] = useState([]);

  // ── Derived state ──
  const isAllSelected = hosts.length > 0 && selectedHostIds.size === hosts.length;
  const isPartiallySelected = selectedHostIds.size > 0 && selectedHostIds.size < hosts.length;

  // ── Effects ──

  // Fetch hosts when storeId changes
  useEffect(() => {
    if (storeId) fetchNonHosts();
  }, [storeId]);

  // Set header checkbox indeterminate state
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  // Keep newly-loaded hosts selected when "select all" is active
  useEffect(() => {
    if (selectAll && hosts.length > 0) {
      setSelectedHostIds(new Set(hosts.map((h) => h.id)));
    }
  }, [hosts, selectAll]);

  // ── Selection helpers ──

  const toggleHostSelection = (id) => {
    setSelectedHostIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    if (selectedHostIds.has(id)) setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (isAllSelected || isPartiallySelected) {
      setSelectedHostIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedHostIds(new Set(hosts.map((h) => h.id)));
      setSelectAll(true);
    }
  };

  // ── Send invites ──

  const handleSendEmailToSelected = async () => {
    if (selectedHostIds.size === 0) {
      messageApi.open({ type: "warning", content: "Please select at least one customer", duration: 3 });
      return;
    }

    const customerIds = Array.from(selectedHostIds);
    const count = customerIds.length;

    Modal.confirm({
      title: "Start New Communications Flow?",
      content: (
        <div>
          <p>
            You are about to send recruitment emails to <strong>{count} customer{count !== 1 ? "s" : ""}</strong>.
          </p>
          <p style={{ color: "#faad14", marginBottom: 0 }}>
            ⚠️ If any of these customers are already in an active communications flow, their current flow will be
            stopped and a new flow will start from the beginning.
          </p>
        </div>
      ),
      okText: "Yes, Send Emails",
      okType: "primary",
      cancelText: "Cancel",
      width: 480,
      onOk: async () => {
        try {
          messageApi.open({ type: "loading", content: "Sending invites...", duration: 0, key: "sendingInvites" });
          await sendHostInvites(storeId, customerIds);
          messageApi.destroy("sendingInvites");
          messageApi.open({ type: "success", content: `Invite sent successfully to ${count} customer(s).`, duration: 3 });
          setSelectedHostIds(new Set());
          fetchNonHosts(1, searchQuery);
        } catch (error) {
          messageApi.destroy("sendingInvites");
          console.error("Error sending invites:", error);
          messageApi.open({
            type: "error",
            content: error.response?.data?.message || "Failed to send invites. Please try again.",
            duration: 5,
          });
        }
      },
    });
  };

  // ── Infinite scroll ──

  const fetchMoreData = useCallback(() => {
    if (!hasMore || hostsLoading) return;
    fetchNonHosts(currentPage + 1, searchQuery, true);
  }, [hasMore, hostsLoading, currentPage, searchQuery]);

  // ── Sorting ──

  const handleSorting = (column) => {
    if (hostsLoading) return;

    const nextDir =
      sorting[column] === "ascending" ? "descending" : sorting[column] === "descending" ? "none" : "ascending";

    const newSorting = Object.keys(sorting).reduce(
      (acc, key) => ({ ...acc, [key]: key === column ? nextDir : "none" }),
      {}
    );
    setSorting(newSorting);

    const columnToApiField = {
      name: "display_name",
      email: "email",
      amount: "total_amount_spent",
      // invitation: "last_invitation_sent",
    };

    if (nextDir === "none") {
      fetchNonHosts(1, searchQuery);
    } else {
      fetchNonHosts(1, searchQuery, false, {
        sort_by: columnToApiField[column],
        sort_order: nextDir === "ascending" ? "asc" : "desc",
      });
    }
  };

  const getTooltipTitle = (col) => {
    if (sorting[col] === "ascending") return "Click to sort descending";
    if (sorting[col] === "descending") return "Click to clear sorting";
    return "Click to sort ascending";
  };

  // ── Filters ──

  const filteredHosts = hosts.filter((host) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      host.name?.toLowerCase().includes(q) ||
      host.email?.toLowerCase().includes(q) ||
      host.product?.toLowerCase().includes(q) ||
      host.amount?.toLowerCase().includes(q)
    );
  });

  const handleClearFilters = () => {
    setSearchQuery("");
    setSorting({ name: "none", email: "none", amount: "none", invitation: "none" });
    fetchNonHosts(1);
  };

  // ── Invitation history modal ──

  // const handleViewInvitationHistory = (history) => {
  //   setSelectedInvitationHistory(history);
  //   setShowInvitationModal(true);
  // };

  // const handleCloseInvitationModal = () => {
  //   setShowInvitationModal(false);
  //   setSelectedInvitationHistory([]);
  // };

  // ── Sortable column header helper ──
  const SortableHeader = ({ label, column }) => (
    <div className="arrows">
      {label}{" "}
      <Tooltip title={getTooltipTitle(column)}>
        <div className="arrow-grid" onClick={() => handleSorting(column)}>
          <CaretUp />
          <CaretDown />
        </div>
      </Tooltip>
    </div>
  );

  // ──────────────────── RENDER ────────────────────

  return (
    <div className="etb-root">
      <header className="etb-header">
        <div className="etb-header-inner">
          <div className="etb-header-left">
            <div>
              <h1 className="etb-header-title">Host Recruitment</h1>
            </div>
          </div>
        </div>
      </header>

      {contextHolder}

      <section className="hosts-section">
        <div className="hosts-card">
          {/* Search & Clear */}
          <div className="hosts-search-row">
            <div className="hosts-search-input-wrap">
              <span className="hosts-search-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16.65" y1="16.65" x2="21" y2="21" />
                </svg>
              </span>
              <input
                className="hosts-search-input"
                placeholder="Search customers by name or email."
                value={searchQuery}
                onChange={(e) => {
                  const q = e.target.value;
                  setSearchQuery(q);
                  fetchNonHosts(1, q.trim() ? q : undefined);
                }}
              />
            </div>
            <button
              className="clear-filters-btn"
              onClick={handleClearFilters}
              disabled={!searchQuery && Object.values(sorting).every((s) => s === "none")}
              title={
                !searchQuery && Object.values(sorting).every((s) => s === "none")
                  ? "No active filters"
                  : "Clear all filters"
              }
            >
              <X size={16} />
              Clear Filters
            </button>
          </div>

          {/* Selection bar */}
          <div className="hosts-selection-bar">
            <span className="hosts-selection-text">{selectedHostIds.size} customers selected</span>
            <Button className="hosts-send-button" onClick={handleSendEmailToSelected} disabled={selectedHostIds.size === 0}>
              <Mail size={16} />
              <span>Send Invites</span>
            </Button>
          </div>

          {/* Table */}
          <div className="table-container" id={scrollableDivId} style={{ height: "500px", overflow: "auto" }}>
            <InfiniteScroll
              dataLength={filteredHosts.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                hostsLoading && (
                  <div className="d-flex justify-content-center py-3">
                    <Spinner animation="grow" size="sm" />
                    <Spinner animation="grow" size="sm" className="mx-2" />
                    <Spinner animation="grow" size="sm" />
                  </div>
                )
              }
              scrollableTarget={scrollableDivId}
              endMessage={
                <div className="text-center py-3 text-muted">
                  {!hasMore && filteredHosts.length > 0 && "No more data to load."}
                </div>
              }
            >
              <div className="table-responsive">
                <Table className="hosts-table" hover>
                  <thead>
                    <tr>
                      <th>
                        <input ref={headerCheckboxRef} type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                      </th>
                      <th><SortableHeader label="Customer Name" column="name" /></th>
                      <th><SortableHeader label="Email" column="email" /></th>
                      <th><SortableHeader label="Purchase Amount" column="amount" /></th>
                      <th>Phone Number</th>
                      {/* <th><SortableHeader label="Last Invitation Sent" column="invitation" /></th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {hostsLoading && filteredHosts.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <Spinner animation="border" />
                        </td>
                      </tr>
                    ) : filteredHosts.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">No customers found</td>
                      </tr>
                    ) : (
                      filteredHosts.map((host) => {
                        // const history = host.invitationHistory || [];
                        // const latest = history.length > 0 ? history[history.length - 1] : null;

                        return (
                          <tr key={host.id}>
                            <td>
                              <input type="checkbox" checked={selectedHostIds.has(host.id)} onChange={() => toggleHostSelection(host.id)} />
                            </td>
                            <td><span>{host.name}</span></td>
                            <td className="hosts-email-cell">{host.email}</td>
                            <td className="hosts-amount-cell">{host.amount}</td>
                            <td>{host.phone}</td>
                            {/* <td className="hosts-invitation-cell">
                              {latest ? (
                                <div className="invitation-compact-wrapper">
                                  <div className="invitation-badge">
                                    <div className="badge-icon"><Mail size={12} /></div>
                                    <div className="badge-content">
                                      <span className="badge-date">{convertUTCToLocalAmPmFormat(latest.invitation_time)[0]}</span>
                                      <span className="badge-time">{convertUTCToLocalAmPmFormat(latest.invitation_time)[1]}</span>
                                    </div>
                                  </div>
                                  {history.length > 1 && (
                                    <div className="history-count-container">
                                      <button className="history-count-badge" onClick={() => handleViewInvitationHistory(history)} title="View all invitations">
                                        <Clock size={10} />
                                        <span>{history.length}</span>
                                      </button>
                                      <span className="see-more-text">See More</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="no-invitation-badge">
                                  <div className="badge-icon muted"><Mail size={12} /></div>
                                  <span>Not sent</span>
                                </div>
                              )}
                            </td> */}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </InfiniteScroll>
          </div>
        </div>
      </section>

      {/* ── Invitation History Modal ── */}
      {/* <Modal
        title={
          <div className="modal-header-custom">
            <div className="modal-header-icon"><Send size={20} color="#fff" /></div>
            <div>
              <div className="modal-title">Invitation History</div>
              <div className="modal-subtitle">Track all sent invitations</div>
            </div>
          </div>
        }
        open={showInvitationModal}
        onCancel={handleCloseInvitationModal}
        footer={null}
        width={560}
        className="invitation-history-modal"
        centered
      >
        {selectedInvitationHistory.length > 0 ? (
          <div className="invitation-history-content">
            <div className="invitation-stats-banner">
              <div className="stat-card">
                <div className="stat-card-icon"><Send size={16} /></div>
                <div className="stat-card-info">
                  <span className="stat-card-value">{selectedInvitationHistory.length}</span>
                  <span className="stat-card-label">Total Sent</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-icon latest-icon"><Clock size={16} /></div>
                <div className="stat-card-info">
                  <span className="stat-card-value">
                    {convertUTCToLocalAmPmFormat(selectedInvitationHistory[selectedInvitationHistory.length - 1].invitation_time)[0]}
                  </span>
                  <span className="stat-card-label">Last Sent</span>
                </div>
              </div>
            </div>

            <div className="invitation-timeline">
              {[...selectedInvitationHistory].reverse().map((invitation, index) => {
                const [date, time] = convertUTCToLocalAmPmFormat(invitation.invitation_time);
                const isLatest = index === 0;
                const total = selectedInvitationHistory.length;
                return (
                  <div key={invitation.id} className={`timeline-item ${isLatest ? "timeline-item--latest" : ""}`} style={{ animationDelay: `${index * 60}ms` }}>
                    {index < total - 1 && <div className="timeline-connector" />}
                    <div className="timeline-dot">{isLatest ? <Send size={12} /> : <Mail size={12} />}</div>
                    <div className="timeline-card">
                      <div className="timeline-card-top">
                        <span className="timeline-card-number">#{index + 1}</span>
                        {isLatest && <span className="timeline-latest-pill">Latest</span>}
                      </div>
                      <div className="timeline-card-body">
                        <div className="timeline-date-row"><Calendar size={14} /><span>{date}</span></div>
                        <div className="timeline-time-row"><Clock size={14} /><span>{time}</span></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="no-invitation-history">
            <div className="empty-state-icon-wrap"><Mail size={32} color="#94a3b8" /></div>
            <p className="empty-state-title">No invitations yet</p>
            <p className="empty-state-desc">Invitations sent to this customer will appear here</p>
          </div>
        )}
      </Modal> */}
    </div>
  );
};

export default HostRecruitment;
